<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  formatPreviewGuideCoordinate,
  resolvePreviewGuideGridStep,
  type PreviewGuideCoordinate,
} from '@/features/preview/previewGuide'

const props = defineProps<{
  visible: boolean
  designWidth: number
  designHeight: number
  surfaceWidth: number
  surfaceHeight: number
  stageScale: number
  coordinate: PreviewGuideCoordinate | null
  label?: string
  showGrid?: boolean
  labelScale?: number
}>()

const canvasRef = ref<HTMLCanvasElement | null>(null)
let renderFrameHandle = 0

const safeDesignWidth = computed(() => Math.max(1, Number(props.designWidth || 1)))
const safeDesignHeight = computed(() => Math.max(1, Number(props.designHeight || 1)))
const safeSurfaceWidth = computed(() => Math.max(1, Number(props.surfaceWidth || 1)))
const safeSurfaceHeight = computed(() => Math.max(1, Number(props.surfaceHeight || 1)))
const surfaceScaleX = computed(() => safeSurfaceWidth.value / safeDesignWidth.value)
const surfaceScaleY = computed(() => safeSurfaceHeight.value / safeDesignHeight.value)
const gridStep = computed(() => resolvePreviewGuideGridStep(props.stageScale))
const coordinateText = computed(() => props.label || (props.coordinate ? formatPreviewGuideCoordinate(props.coordinate) : ''))
const labelStyle = computed(() => {
  const coordinate = props.coordinate
  const x = coordinate ? coordinate.x * surfaceScaleX.value : 0
  const y = coordinate ? coordinate.y * surfaceScaleY.value : 0
  const left = Math.min(Math.max(6, x + 8), Math.max(6, safeSurfaceWidth.value - 112))
  const top = Math.min(Math.max(6, y + 8), Math.max(6, safeSurfaceHeight.value - 30))
  const labelScale = Number.isFinite(props.labelScale) && props.labelScale ? props.labelScale : 1
  const fontSize = Math.max(11, Math.min(18, (12 * labelScale) / Math.max(0.75, props.stageScale || 1)))
  return {
    left: `${left}px`,
    top: `${top}px`,
    fontSize: `${fontSize}px`,
  }
})

function scheduleRender() {
  if (renderFrameHandle) return
  renderFrameHandle = requestAnimationFrame(() => {
    renderFrameHandle = 0
    renderGrid()
  })
}

function renderGrid() {
  const canvas = canvasRef.value
  if (!canvas) return
  const width = safeSurfaceWidth.value
  const height = safeSurfaceHeight.value
  const dpr = Math.max(1, window.devicePixelRatio || 1)
  const bufferWidth = Math.max(1, Math.round(width * dpr))
  const bufferHeight = Math.max(1, Math.round(height * dpr))
  if (canvas.width !== bufferWidth) canvas.width = bufferWidth
  if (canvas.height !== bufferHeight) canvas.height = bufferHeight
  canvas.style.width = `${width}px`
  canvas.style.height = `${height}px`
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, width, height)
  if (!props.visible || props.showGrid === false) return

  const stepX = Math.max(1, gridStep.value * surfaceScaleX.value)
  const stepY = Math.max(1, gridStep.value * surfaceScaleY.value)
  const minorLineColor = 'rgba(226, 232, 240, 0.12)'
  const majorLineColor = 'rgba(226, 232, 240, 0.34)'
  ctx.setLineDash([])
  for (let x = 0, index = 0; x <= width; x += stepX, index += 1) {
    const px = Math.round(x) + 0.5
    const isMajor = index % 4 === 0
    ctx.lineWidth = 1
    ctx.strokeStyle = isMajor ? majorLineColor : minorLineColor
    ctx.beginPath()
    ctx.moveTo(px, 0)
    ctx.lineTo(px, height)
    ctx.stroke()
  }
  for (let y = 0, index = 0; y <= height; y += stepY, index += 1) {
    const py = Math.round(y) + 0.5
    const isMajor = index % 4 === 0
    ctx.lineWidth = 1
    ctx.strokeStyle = isMajor ? majorLineColor : minorLineColor
    ctx.beginPath()
    ctx.moveTo(0, py)
    ctx.lineTo(width, py)
    ctx.stroke()
  }
}

watch(
  () => [
    props.visible,
    safeSurfaceWidth.value,
    safeSurfaceHeight.value,
    props.stageScale,
    gridStep.value,
    props.showGrid,
  ],
  scheduleRender,
)

onMounted(scheduleRender)

onBeforeUnmount(() => {
  if (renderFrameHandle) {
    cancelAnimationFrame(renderFrameHandle)
    renderFrameHandle = 0
  }
})
</script>

<template>
  <div v-show="visible" class="preview-guide-overlay" aria-hidden="true">
    <canvas v-show="showGrid !== false" ref="canvasRef" class="preview-guide-overlay__canvas" />
    <div v-if="coordinateText" class="preview-guide-overlay__label" :style="labelStyle">
      {{ coordinateText }}
    </div>
  </div>
</template>

<style scoped>
.preview-guide-overlay {
  position: absolute;
  inset: 0;
  z-index: 8;
  pointer-events: none;
}

.preview-guide-overlay__canvas {
  position: absolute;
  inset: 0;
  display: block;
}

.preview-guide-overlay__label {
  position: absolute;
  padding: 2px 5px;
  color: #e5eefb;
  background: #111827;
  font-family: Consolas, 'Microsoft YaHei', sans-serif;
  line-height: 1.3;
  white-space: nowrap;
}
</style>
